import React, { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const WHATSAPP_NUMBER = '556296481343'; // Substitua pelo número real do WhatsApp

const OrderModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    protein: '',
    carbs: '',
    fat: '',
    fiber: '',
    meals: '',
    plan: 'Semanal',
    preferences: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Formatar mensagem para WhatsApp
    const message = `*NOVO PEDIDO - FEATS*

*Dados Pessoais:*
Nome: ${formData.name}
E-mail: ${formData.email}
WhatsApp: ${formData.phone}
Cidade/Região: ${formData.city}

*Prescrição Nutricional (por refeição):*
Proteína: ${formData.protein}g
Carboidrato: ${formData.carbs}g
Feijão: ${formData.fat}g
Legumes: ${formData.fiber || 'Não informado'}g

*Plano e Quantidade:*
Plano: ${formData.plan}
Refeições por semana: ${formData.meals}

*Preferências Alimentares:*
${formData.preferences || 'Nenhuma preferência especificada'}

---
_Aguardo orçamento personalizado!_`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
    
    // Limpar formulário e fechar modal
    setFormData({
      name: '',
      email: '',
      phone: '',
      city: '',
      protein: '',
      carbs: '',
      fat: '',
      fiber: '',
      meals: '',
      plan: 'Semanal',
      preferences: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Montar Pedido</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">WhatsApp</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="(62) 99999-9999"
                />
              </div>
              <div>
                <Label htmlFor="city">Cidade/Região *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="Ex: Goiânia/GO"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Prescrição Nutricional (por refeição)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="protein">Proteína (g) *</Label>
                <Input
                  id="protein"
                  name="protein"
                  type="number"
                  value={formData.protein}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="150"
                />
              </div>
              <div>
                <Label htmlFor="carbs">Carboidrato (g) *</Label>
                <Input
                  id="carbs"
                  name="carbs"
                  type="number"
                  value={formData.carbs}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="200"
                />
              </div>
              <div>
                <Label htmlFor="fat">Feijão (g) *</Label>
                <Input
                  id="fat"
                  name="fat"
                  type="number"
                  value={formData.fat}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="50"
                />
              </div>
              <div>
                <Label htmlFor="fiber">Legumes (g)</Label>
                <Input
                  id="fiber"
                  name="fiber"
                  type="number"
                  value={formData.fiber}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="25"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan">Plano de Entrega *</Label>
              <select
                id="plan"
                name="plan"
                value={formData.plan}
                onChange={handleChange}
                required
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="Semanal">Semanal</option>
                <option value="Quinzenal">Quinzenal</option>
                <option value="Mensal">Mensal</option>
              </select>
            </div>
            <div>
              <Label htmlFor="meals">Refeições por semana *</Label>
              <Input
                id="meals"
                name="meals"
                type="number"
                value={formData.meals}
                onChange={handleChange}
                required
                className="mt-1"
                placeholder="14"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="preferences">Preferências alimentares ou detalhes (opcional)</Label>
            <Textarea
              id="preferences"
              name="preferences"
              value={formData.preferences}
              onChange={handleChange}
              className="mt-1"
              rows={4}
              placeholder="Ex: sem lactose, evitar carne vermelha, serão duas dietas diferentes, etc..."
            />
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
            <MessageCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800">
              <strong>Próximo passo:</strong> Ao clicar em "Enviar para WhatsApp", você será redirecionado para 
              nossa conversa com todos os dados preenchidos. Responderemos rapidamente com seu orçamento personalizado!
            </p>
          </div>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
              <MessageCircle className="mr-2 h-5 w-5" />
              Enviar para WhatsApp
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderModal;
